import Box from '@mui/material/Box';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { FC, ReactNode } from 'react';
import { Button } from 'src/common';
import { useEdrGlobalFiltersAccess } from 'src/mindtime/hooks/useEdrGlobalFiltersAccess';
import { SecurityClientFilter } from './SecurityClientFilter';
import { SecurityEndpointFilter } from './SecurityEndpointFilter';
import { SecuritySearchableFilter } from './SecuritySearchableFilter';
import type { SecuritySearchableFilterOption } from './SecuritySearchableFilter';
import { SecurityToolbarFilter, SecurityToolbarSearch } from './securityDashboardToolbar';
import type { SecurityToolbarFilterOption } from './securityDashboardToolbar';

export type EdrToolbarFilter = {
  id: string;
  label: string;
  icon?: ReactNode;
  options?: SecurityToolbarFilterOption[];
  value?: string;
  onChange?: (value: string) => void;
  multiple?: boolean;
  /**
   * - `resellerClient` — searchable org picker (SecurityClientFilter; reseller only)
   * - `searchableEndpoint` — searchable endpoints picker (SecurityEndpointFilter; all roles)
   * - `searchable` — generic searchable picker (SecuritySearchableFilter; page supplies options/onSearch)
   */
  variant?: 'default' | 'resellerClient' | 'searchableEndpoint' | 'searchable';
  /** Page-local Organization filter; narrows endpoint options when variant is searchableEndpoint. */
  pageOrganizationId?: string;
  /** Used when variant is `searchable`. */
  searchableOptions?: SecuritySearchableFilterOption[];
  searchableLoading?: boolean;
  searchableOnSearch?: (query: string) => void;
};

type EdrDataToolbarProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  filters: EdrToolbarFilter[];
  onReset?: () => void;
  resetLabel?: string;
  trailing?: ReactNode;
};

const isGlobalScopeFilter = (filter: EdrToolbarFilter) =>
  filter.id === 'date' || filter.variant === 'searchableEndpoint';

export const EdrDataToolbar: FC<EdrDataToolbarProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filters,
  onReset,
  resetLabel = 'Reset',
  trailing,
}) => {
  const { canDateRange, canOrganization, canSearch, canEndpoint } = useEdrGlobalFiltersAccess();

  const renderFilter = (filter: EdrToolbarFilter) => {
    if (filter.id === 'date' && !canDateRange) {
      return null;
    }
    if (filter.variant === 'resellerClient' && !canOrganization) {
      return null;
    }
    if (filter.variant === 'searchableEndpoint' && !canEndpoint) {
      return null;
    }

    if (filter.variant === 'resellerClient' && filter.onChange) {
      return (
        <SecurityClientFilter
          key={filter.id}
          value={filter.value || null}
          onChange={(clientId) => filter.onChange?.(clientId ?? '')}
          pillLabel={filter.label}
          allLabel={`All ${filter.label}`}
          searchPlaceholder={`Search ${filter.label.toLowerCase()}...`}
          emptyMessage={`No ${filter.label.toLowerCase()} found`}
          selectedFallbackLabel={`Selected ${filter.label.toLowerCase()}`}
          showFilterIcon={false}
          fullWidth={false}
        />
      );
    }

    if (filter.variant === 'searchableEndpoint') {
      // Default: session-global endpoint via useWazuhScope. Pass onChange only for rare controlled cases.
      const isControlled = filter.onChange !== undefined;
      return (
        <SecurityEndpointFilter
          key={filter.id}
          {...(isControlled
            ? {
                value: filter.value || null,
                onChange: (endpointId: string | null) => filter.onChange?.(endpointId ?? ''),
              }
            : {})}
          pageOrganizationId={filter.pageOrganizationId}
          pillLabel={filter.label}
          allLabel={`All ${filter.label}`}
          searchPlaceholder={`Search ${filter.label.toLowerCase()}...`}
          emptyMessage={`No ${filter.label.toLowerCase()} found`}
          selectedFallbackLabel={`Selected ${filter.label.toLowerCase()}`}
          showFilterIcon={false}
          fullWidth={false}
          multiple={filter.multiple}
        />
      );
    }

    if (filter.variant === 'searchable' && filter.onChange && filter.searchableOnSearch) {
      return (
        <SecuritySearchableFilter
          key={filter.id}
          value={filter.value || null}
          onChange={(selected) => filter.onChange?.(selected ?? '')}
          options={filter.searchableOptions ?? []}
          onSearch={filter.searchableOnSearch}
          loading={filter.searchableLoading}
          pillLabel={filter.label}
          allLabel={`All ${filter.label}`}
          searchPlaceholder={`Search ${filter.label.toLowerCase()}...`}
          emptyMessage={`No ${filter.label.toLowerCase()} found`}
          selectedFallbackLabel={`Selected ${filter.label.toLowerCase()}`}
          showFilterIcon={false}
          fullWidth={false}
          multiple={filter.multiple}
          leadingIcon={filter.icon}
        />
      );
    }

    return (
      <SecurityToolbarFilter
        key={filter.id}
        label={filter.label}
        icon={filter.icon}
        options={filter.options}
        value={filter.value}
        onChange={filter.onChange}
        multiple={filter.multiple}
      />
    );
  };

  const pageFilters = filters.filter((filter) => !isGlobalScopeFilter(filter));
  // Endpoint + Date Range sit with Reset/Export (global scope affordance).
  const scopeFilters = filters.filter(isGlobalScopeFilter);

  return (
  <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 10, width: '100%' }}>
    {canSearch ? (
    <Box style={{ width: 240, minWidth: 200, flex: '0 0 auto' }}>
      <SecurityToolbarSearch
        value={searchValue}
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
      />
    </Box>
    ) : null}
    {pageFilters.map(renderFilter)}
    <Box style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {scopeFilters.map(renderFilter)}
      {onReset ? (
        <Button
          variant="text"
          size="small"
          startIcon={<RefreshIcon style={{ fontSize: 16 }} />}
          onClick={onReset}
          style={{ textTransform: 'none', color: '#64748B', fontSize: 13, fontWeight: 500 }}
        >
          {resetLabel}
        </Button>
      ) : null}
      {trailing}
    </Box>
  </Box>
  );
};
